import { ReactNode, useCallback, useMemo } from 'react';

import { useCheckAnswer } from 'approot/activity-builder/check-answer/check-answer.hooks';
import { ViewProps } from './multiple-choice.constants';
import { checkAnswerVar } from 'approot/activity-builder/check-answer/check-answer.apollo';
import { resetComponentAnswer } from 'approot/activity-builder/check-answer/check-answer.utils';
import SubActivityView from '../sub-activity/sub-activity-view';
import { CheckAnswerButtonPrimary } from 'approot/activity-builder/check-answer/check-answer.style';
import MultipleChoiceViewOptions from './multiple-choice-view-options';
import { useFindComponentDimensions } from '../component.hooks';
import { isSingleSelect } from './logic/is-single-select';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import ActivitySheet from '../shared/sheet/activity-sheet';
import ClassroomAnswerTabs, {
  CLASS_VIEW,
  INDIVIDUAL_VIEW,
  QUESTION_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import MultipleChoiceClassResponses from 'approot/activity-builder/classroom/multiple-choice/multiple-choice-class-responses';
import MultipleChoiceIndividualResponses from 'approot/activity-builder/classroom/multiple-choice/multiple-choice-individual-responses';
import * as Styled from './multiple-choice-check-answer.style';
import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import useInitialClassroomTab from 'approot/activity-builder/classroom/use-initial-classroom-tab';
import {
  ActivitySheetGridMax,
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../shared/sheet/activity-sheet.style';
import ActivitySheetSaveStatus from '../shared/sheet/activity-sheet-save-status';

type Props = ViewProps & {
  isPublished: boolean;
  banner?: ReactNode;
  canClearModelledResponse?: boolean;
  onClose: () => void;
  onSubmitAnswer?: () => void;
};

const MultipleChoiceCheckAnswerFreeTeacher = (props: Props) => {
  const {
    isPublished,
    properties,
    subActivityProperties,
    id,
    type,
    onClose,
    onSubmitAnswer,
    canClearModelledResponse = true,
  } = props;

  const { activityBuilderRespondFlag } = useActivityBuilderFlags();

  const { checkAnswerData, hasAnswered } = useCheckAnswer({
    componentId: id,
  });
  const componentAnswers = checkAnswerData[id];

  const { width } = useFindComponentDimensions(id);

  const { multipleChoice } = properties;

  const selectedAnswers = componentAnswers?.multiAnswer?.answerRefs || [];

  const isOneOptionCorrect = useMemo(
    () =>
      isSingleSelect({
        answerMode: multipleChoice.answerMode,
        isFreeAnswerMultiSelect: multipleChoice.isFreeAnswerMultiSelect,
        options: multipleChoice.options,
      }),
    [multipleChoice]
  );

  const handleCheckAnswer = useCallback(() => {
    if (componentAnswers) {
      checkAnswerVar({
        ...checkAnswerData,
        [id]: {
          ...componentAnswers,
          hasChecked: true,
        },
      });
      onSubmitAnswer?.();
    }
  }, [id, componentAnswers, checkAnswerData, onSubmitAnswer]);

  const { tab, setTab } = useInitialClassroomTab({
    isVisible: true,
    canRespond: isPublished && activityBuilderRespondFlag,
    componentId: id,
  });

  return (
    <ActivitySheet
      isVisible
      title="Multiple choice answers view modal"
      hasBackgroundColor
      customActions={
        <ActivitySheetSaveStatus
          title="Multiple choice pick your answer"
          onClose={() => {
            setTab(INDIVIDUAL_VIEW);
            onClose();
          }}
        />
      }
      tabsContent={
        <ClassroomAnswerTabs
          isPublished={isPublished && activityBuilderRespondFlag}
          canClearModelledResponse={canClearModelledResponse}
          tab={tab}
          onSelect={option => setTab(option)}
          componentId={id}
          onCloseActivitySheet={() => {
            setTab(INDIVIDUAL_VIEW);
            onClose();
          }}
        />
      }
      banner={props.banner}
    >
      <ActivitySheetInnerContent id="content">
        {tab === QUESTION_VIEW && (
          <ActivitySheetInnerMax>
            <Styled.MultipleChoiceCheckAnswerQuestion
              $noMarginBottom={!isOneOptionCorrect}
            >
              <SubActivityView
                withTTSButton
                componentId={id}
                componentType={type}
                subActivityDetails={subActivityProperties}
              >
                {!isOneOptionCorrect && (
                  <Styled.MultipleChoiceCheckAnswerText>
                    You can select more than one.
                  </Styled.MultipleChoiceCheckAnswerText>
                )}

                <MultipleChoiceViewOptions
                  {...props}
                  isResponsive
                  showAnswer={false}
                  width={width}
                />

                {hasAnswered && (
                  <Styled.MultipleChoiceFreeAnswerText>
                    Your answer has been submitted.{' '}
                    <LinkButton
                      type="button"
                      $overrideFont
                      onClick={() => resetComponentAnswer(id, checkAnswerData)}
                    >
                      Change Answer
                    </LinkButton>
                  </Styled.MultipleChoiceFreeAnswerText>
                )}
              </SubActivityView>
            </Styled.MultipleChoiceCheckAnswerQuestion>

            <Styled.MultipleChoiceCheckAnswer>
              {!hasAnswered && (
                <CheckAnswerButtonPrimary
                  type="button"
                  disabled={selectedAnswers.length === 0}
                  onClick={handleCheckAnswer}
                >
                  Submit Answer
                </CheckAnswerButtonPrimary>
              )}
            </Styled.MultipleChoiceCheckAnswer>
          </ActivitySheetInnerMax>
        )}
        {tab === CLASS_VIEW && (
          <ActivitySheetInnerMax>
            <MultipleChoiceClassResponses
              component={props}
              subActivityProperties={subActivityProperties}
            />
          </ActivitySheetInnerMax>
        )}
        {tab === INDIVIDUAL_VIEW && (
          <ActivitySheetGridMax>
            <MultipleChoiceIndividualResponses
              componentId={id}
              component={props}
            />
          </ActivitySheetGridMax>
        )}
      </ActivitySheetInnerContent>
    </ActivitySheet>
  );
};

export default MultipleChoiceCheckAnswerFreeTeacher;
